import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import MetricCard from '@/components/MetricCard';
import { formatGBP } from '@/lib/utils';
import {
  AlertCircle,
  CalendarCheck,
  CalendarClock,
  Loader2,
  PoundSterling,
  RefreshCw,
  TrendingUp,
  UserPlus,
  Users,
  Wallet,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

function CommissionExplainer() {
  return (
    <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-6">
      <h3 className="text-sm font-semibold text-slate-900">Commission model</h3>
      <p className="text-sm text-slate-600 mt-1">
        BeautyMateUK takes <span className="font-semibold text-slate-900">20%</span> commission from each
        service price. Partners keep <span className="font-semibold text-slate-900">80%</span> of the service price
        plus <span className="font-semibold text-slate-900">100%</span> of the travel fee.
      </p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div className="rounded-lg bg-slate-50 p-3 border border-slate-200">
          <p className="text-xs text-slate-500">Example service</p>
          <p className="font-semibold text-slate-900">£35.00</p>
        </div>
        <div className="rounded-lg bg-rose-50 p-3 border border-rose-200">
          <p className="text-xs text-rose-700">Platform (20%)</p>
          <p className="font-semibold text-rose-700">£7.00</p>
        </div>
        <div className="rounded-lg bg-emerald-50 p-3 border border-emerald-200">
          <p className="text-xs text-emerald-700">Partner (80% + travel)</p>
          <p className="font-semibold text-emerald-700">£33.00*</p>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">
        *with £5 travel fee → £28 service share + £5 travel = £33.00
      </p>
    </div>
  );
}

function PhaseStatusPanel() {
  return (
    <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 p-6 text-white">
      <h3 className="text-sm font-semibold">Phase 1 status</h3>
      <p className="text-sm text-slate-300 mt-1">Admin Dashboard · Core API · MongoDB</p>
      <ul className="mt-4 space-y-2 text-sm text-slate-200">
        <li>✓ JWT-secured admin login</li>
        <li>✓ Partner approve / reject / suspend</li>
        <li>✓ Booking status workflow</li>
        <li>✓ Commission &amp; payout reporting</li>
        <li>✓ 2dsphere geo-index ready</li>
      </ul>
      <p className="mt-4 text-xs text-slate-400">Customer &amp; Partner apps — Phase 2</p>
    </div>
  );
}

export default function Dashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/admin/dashboard');
      setMetrics(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Operational overview · Harrow, London</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchMetrics}
          disabled={loading}
          data-testid="btn-refresh-metrics"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </header>

      {error && (
        <div
          className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 flex items-center gap-2"
          data-testid="metrics-error"
        >
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {loading && !metrics ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading metrics...
        </div>
      ) : (
        <>
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="metrics-grid">
            <MetricCard testId="metric-total-bookings" title="Total bookings" value={metrics?.total_bookings ?? 0} icon={CalendarCheck} accent="navy" />
            <MetricCard testId="metric-bookings-today" title="Bookings today" value={metrics?.bookings_today ?? 0} icon={CalendarClock} accent="sky" />
            <MetricCard
              testId="metric-total-revenue"
              title="Total revenue"
              value={formatGBP(metrics?.total_revenue)}
              icon={PoundSterling}
              accent="emerald"
              subtitle="From confirmed / in-progress / completed bookings"
            />
            <MetricCard
              testId="metric-platform-commission"
              title="Platform commission"
              value={formatGBP(metrics?.platform_commission)}
              icon={TrendingUp}
              accent="rose"
              subtitle="20% of service price"
            />
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              testId="metric-partner-payouts"
              title="Partner payouts"
              value={formatGBP(metrics?.partner_payouts)}
              icon={Wallet}
              accent="violet"
              subtitle="80% service + 100% travel"
            />
            <MetricCard testId="metric-pending-partners" title="Pending partners" value={metrics?.pending_partners ?? 0} icon={UserPlus} accent="amber" />
            <MetricCard testId="metric-approved-partners" title="Approved partners" value={metrics?.approved_partners ?? 0} icon={Users} accent="emerald" />
            <MetricCard testId="metric-cancelled-bookings" title="Cancelled bookings" value={metrics?.cancelled_bookings ?? 0} icon={XCircle} accent="rose" />
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-2">
            <CommissionExplainer />
            <PhaseStatusPanel />
          </section>
        </>
      )}
    </div>
  );
}
